import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { MapPin } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { RootState } from '@/store';
import axiosService from '@/utils/axios';
import { authHref } from '@/utils/authReturn';
import { DirectoryResponse, readEmbeddedDirectory } from './embeddedDirectory';

const DIRECTORY_STALE_MS = 5 * 60 * 1000;

const directoryPath = (skillSlug?: string, placeSlug?: string) =>
    ['/seo/directory', skillSlug, placeSlug].filter(Boolean).join('/');

const DirectoryHub = () => {
    const { skillSlug, placeSlug } = useParams();
    const { pathname, search } = useLocation();
    const [searchParams] = useSearchParams();
    const isGuest = useSelector((state: RootState) => !state.auth.user);
    const pageParam = searchParams.get('page');
    const seeded = useMemo(() => {
        const data = readEmbeddedDirectory(skillSlug, placeSlug, pageParam);
        return data ? { data, updatedAt: Date.now() } : null;
    }, [skillSlug, placeSlug, pageParam]);
    const query = useQuery({
        queryKey: ['directory', skillSlug ?? null, placeSlug ?? null, pageParam ?? '1'],
        queryFn: () => axiosService
            .get<DirectoryResponse>(directoryPath(skillSlug, placeSlug), {
                params: pageParam ? { page: pageParam } : undefined,
            })
            .then((response) => response.data),
        retry: false,
        staleTime: DIRECTORY_STALE_MS,
        ...(seeded
            ? { initialData: seeded.data, initialDataUpdatedAt: seeded.updatedAt }
            : {}),
    });

    const status = (query.error as { response?: { status?: number } } | null)?.response?.status;
    const pageMeta = query.data ?? (status === 404 ? {
        documentTitle: 'No matches | JamWith',
        metaDescription: 'That skill or city is not on JamWith yet.',
        robots: 'noindex, follow',
    } : null);
    usePageMeta(pageMeta);

    useEffect(() => {
        document.getElementById('app-scroll-container')?.scrollTo(0, 0);
    }, [skillSlug, placeSlug, pageParam]);

    if (!query.data && query.isError && status === 404) {
        return (
            <main className="mx-auto w-full max-w-3xl px-4 py-10">
                <h1 className="text-2xl font-bold text-eerieblack">No matches</h1>
                <p className="mt-2 text-gray-600">That skill or city is not on JamWith yet.</p>
                <Link className="mt-4 inline-block text-purple-700 underline" to="/directory">Browse musicians</Link>
            </main>
        );
    }

    if (!query.data && query.isError) {
        return (
            <main className="mx-auto w-full max-w-3xl px-4 py-10">
                <h1 className="text-2xl font-bold text-eerieblack">Couldn&apos;t load musicians</h1>
                <p className="mt-2 text-gray-600">Something went wrong loading this page.</p>
                <button
                    type="button"
                    className="mt-4 text-purple-700 underline"
                    onClick={() => { query.refetch(); }}
                >
                    Try again
                </button>
            </main>
        );
    }

    if (!query.data) {
        return (
            <main className="mx-auto w-full max-w-3xl px-4 py-10">
                <p className="text-gray-600">Loading musicians…</p>
            </main>
        );
    }

    const page = query.data;
    const heading = directoryHeading(page);

    return (
        <main className="mx-auto w-full max-w-3xl px-4 py-8">
            <h1 className="text-2xl font-bold text-eerieblack">{heading}</h1>
            {!page.skill && page.summary && <p className="mt-2 text-gray-600">{page.summary}</p>}

            {!!page.skills?.length && (
                <ul className="mt-6 grid gap-2 sm:grid-cols-2">
                    {page.skills.map((skill) => (
                        <li key={skill.slug}>
                            <Link className="text-purple-700 underline" to={skill.href}>
                                {skill.displayName} ({skill.count})
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {!!page.places?.length && page.skill && (
                <ul className="mt-6 flex flex-wrap gap-2">
                    {page.places.map((place) => (
                        <li key={place.slug}>
                            <Link
                                className="inline-block rounded-full border border-gray-300 px-3 py-1 text-sm text-gray-700"
                                to={`/directory/${page.skill?.slug}/${place.slug}`}
                            >
                                {place.displayName} ({place.count})
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {!!page.profiles?.length && (
                <ul className="mt-6 divide-y divide-gray-200 rounded-lg bg-white">
                    {page.profiles.map((profile) => (
                        <li key={profile.username}>
                            <Link className="flex flex-col px-4 py-3 hover:bg-gray-50" to={profile.href}>
                                <span className="font-semibold text-eerieblack">{profile.displayName}</span>
                                {(profile.place || profile.city) && (
                                    <span className="mt-0.5 inline-flex items-center gap-1 self-start text-sm text-gray-500">
                                        <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                        {profile.place || profile.city}
                                    </span>
                                )}
                                {!!profile.skills?.length && (
                                    <span className="mt-1 text-sm text-gray-600">
                                        {profile.skills.slice(0, 3).join(', ')}
                                    </span>
                                )}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {(page.pageCount ?? 1) > 1 && (
                <nav className="mt-6 flex items-center justify-between text-sm" aria-label="Pages">
                    {page.page && page.page > 1 ? (
                        <Link className="text-purple-700 underline" to={directoryPageHref(pathname, searchParams, page.page - 1)}>Previous</Link>
                    ) : <span />}
                    <span className="text-gray-600">Page {page.page} of {page.pageCount}</span>
                    {page.page && page.pageCount && page.page < page.pageCount ? (
                        <Link className="text-purple-700 underline" to={directoryPageHref(pathname, searchParams, page.page + 1)}>Next</Link>
                    ) : <span />}
                </nav>
            )}

            {isGuest && (
                <p className="mt-8 text-sm text-gray-600">
                    <Link className="font-semibold text-purple-700 underline" to={authHref('signup', `${pathname}${search}`)}>
                        Join free
                    </Link>
                    {' '}
                    to collab with the music community on JamWith.
                </p>
            )}
        </main>
    );
};

function directoryHeading(page: DirectoryResponse): string {
    if (!page.skill || typeof page.total !== 'number') return page.h1 || '';
    const place = page.place?.displayName;
    const subject = place ? `${page.skill.displayName} in ${place}` : `${page.skill.displayName} profiles`;
    return `${subject} on JamWith (${page.total})`;
}

function directoryPageHref(pathname: string, searchParams: URLSearchParams, page: number): string {
    const params = new URLSearchParams(searchParams);
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
}

export default DirectoryHub;
